import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Filter, Sigma, X } from "lucide-react";
import {
  claimQuestReward,
  confirmReadingObjective,
  creditQuestRewardToInventory,
  debitMetamorphPotion,
  fetchPotionCounts,
  fetchQuestJournal,
  fetchTodayQuests,
  formatQuestCountdown,
  markQuestsRead,
  QUEST_POLL_INTERVAL_MS,
} from "../utils/questApi";
import { formatQuestProgressLabel } from "../utils/questProgressHelpers";
import { resolveQuestObjectiveLabel, splitCommonGemBourseNote } from "../utils/dailyQuestGenerator";
import { processQuestCompletionSideEffects } from "../utils/questCompletionHandler";
import { dispatchNotificationsChanged } from "../utils/levelHistoryApi";
import { QUEST_PROGRESS_CHANGED_EVENT, notifyQuestProgressChanged } from "../utils/questProgressReporter";
import { QUESTS_API_URL } from "../config/questsApi";
import { getQuestCharacter, getQuestCharacterPortrait } from "../constants/questCharacters";
import {
  formatQuestJournalDate,
  personalizeQuestIntro,
} from "../utils/questDialogueText";
import {
  QUEST_JOURNAL_SEARCH_SCOPES,
  readQuestJournalDetailScroll,
  readQuestJournalFilters,
  readQuestJournalLastSeenActiveDate,
  readQuestJournalSelectedDate,
  readQuestJournalSidebarScroll,
  writeQuestJournalDetailScroll,
  writeQuestJournalFilters,
  writeQuestJournalLastSeenActiveDate,
  writeQuestJournalSelectedDate,
  writeQuestJournalSidebarScroll,
  resolveQuestJournalSelectedDate,
} from "../utils/questJournalPreference";
import { normalizeRegisterSearch } from "../utils/registerWordListDisplay";
import {
  QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS,
  QUEST_COMPLETED_OBJECTIVE_DISPLAY,
} from "../constants/questPreferences";
import {
  DEFAULT_QUEST_JOURNAL_FILTERS,
  isQuestJournalFilterActive,
  prepareQuestJournalFiltersForApply,
  sanitizeQuestJournalFilters,
} from "../constants/questJournalFilters";
import { useCustomScrollbar } from "../hooks/useCustomScrollbar";
import CircleCheckIcon from "./IconComponent/CircleCheckIcon";
import QuestJournalRewards from "./QuestJournalRewards";
import QuestChestRewardModal from "./QuestChestRewardModal";
import QuestJournalFilterModal from "./QuestJournalFilterModal";
import QuestJournalStatsModal from "./QuestJournalStatsModal";
import QuestMetamorphModal from "./QuestMetamorphModal";
import MenuIconTooltip from "./MenuIconTooltip";
import VocabInfoTip from "./VocabInfoTip";
import liqueurMetamorpheImg from "../assets/liqueurDuMetamorphe.png";
import { METAMORPH_POTION_ID } from "../constants/potions";
import "./QuestView.css";

function matchesQuestJournalSearch(entry, normalizedQuery, searchScope, playerName) {
  if (!normalizedQuery) return true;

  if (searchScope === QUEST_JOURNAL_SEARCH_SCOPES.CONTENT) {
    const intro = personalizeQuestIntro(entry?.dialogue?.intro || "", playerName);
    const content = normalizeRegisterSearch(intro);
    return content.includes(normalizedQuery);
  }

  const title = normalizeRegisterSearch(entry?.dialogue?.title || "");
  return title.includes(normalizedQuery);
}

function QuestJournalScrollbar({
  trackRef,
  scrollbar,
  variant,
  onTrackPointerDown,
  onThumbPointerDown,
}) {
  return (
    <div
      ref={trackRef}
      className={[
        "quest-journal-scrollbar",
        `quest-journal-scrollbar--${variant}`,
        !scrollbar.visible && "quest-journal-scrollbar--hidden",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true"
      onPointerDown={onTrackPointerDown}
    >
      {scrollbar.visible ? (
        <div
          className="quest-journal-scrollbar-thumb"
          style={{
            height: `${scrollbar.height}px`,
            transform: `translateY(${scrollbar.top}px)`,
          }}
          onPointerDown={onThumbPointerDown}
        />
      ) : null}
    </div>
  );
}

const READING_HONOR_OPTIONS = [
  { value: "pending", label: "En cours" },
  { value: "completed", label: "Accompli" },
];

function ReadingHonorSelect({ objective, onConfirmed }) {
  const [submitting, setSubmitting] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isLocked =
    objective.honorLocked || objective.status === "completed" || submitting;
  const value = objective.status === "completed" ? "completed" : "pending";
  const selectedLabel =
    READING_HONOR_OPTIONS.find((option) => option.value === value)?.label || "En cours";

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const handleSelect = async (nextValue) => {
    setDropdownOpen(false);
    if (isLocked || nextValue === value || nextValue !== "completed") return;

    setSubmitting(true);
    try {
      const data = await confirmReadingObjective(objective.id);
      onConfirmed(data);
      dispatchNotificationsChanged();
      notifyQuestProgressChanged();
      await processQuestCompletionSideEffects(data);
    } catch (err) {
      console.error("Validation lecture :", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className={[
        "quest-reading-honor-dropdown",
        dropdownOpen && "quest-reading-honor-dropdown--open",
        isLocked && "quest-reading-honor-dropdown--disabled",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="quest-reading-honor-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        aria-label="Validation de l'objectif de lecture"
        disabled={isLocked}
        onClick={() => {
          if (!isLocked) setDropdownOpen((open) => !open);
        }}
      >
        <span className="quest-reading-honor-dropdown__label">{selectedLabel}</span>
        <ChevronDown
          size={12}
          strokeWidth={2}
          className="quest-reading-honor-dropdown__chevron"
          aria-hidden="true"
        />
      </button>

      {dropdownOpen && !isLocked ? (
        <ul className="quest-reading-honor-dropdown__menu" role="listbox">
          {READING_HONOR_OPTIONS.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={[
                  "quest-reading-honor-dropdown__option",
                  option.value === value && "quest-reading-honor-dropdown__option--selected",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function QuestProgressBar({ completedCount = 0, totalObjectives = 4, isActive = false }) {
  const slots = Array.from({ length: totalObjectives }, (_, index) => index);
  const isDone = totalObjectives > 0 && completedCount >= totalObjectives;
  const status = isDone ? "done" : isActive ? "ongoing" : "failed";

  const statusLabel =
    status === "done" ? "Terminée" : status === "ongoing" ? "En cours" : "Échouée";

  const statusAriaLabel =
    status === "done"
      ? "Quête terminée"
      : status === "ongoing"
        ? "Quête en cours"
        : "Quête échouée";

  return (
    <div
      className="quest-journal-progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={totalObjectives}
      aria-valuenow={completedCount}
      aria-label={`Avancement de la quête : ${completedCount} sur ${totalObjectives} objectifs`}
    >
      <div className="quest-journal-progress-row">
        {slots.map((index) => (
          <div key={index} className="quest-journal-progress-unit">
            {index > 0 ? (
              <span
                className={`quest-journal-progress-bridge${
                  index <= completedCount ? " quest-journal-progress-bridge--done" : ""
                }`}
                aria-hidden="true"
              />
            ) : null}
            <span
              className={`quest-journal-progress-slot${
                index < completedCount ? " quest-journal-progress-slot--done" : ""
              }`}
            >
              {index < completedCount ? "✓" : index + 1}
            </span>
          </div>
        ))}
      </div>
      <p className="quest-journal-progress-label">
        {completedCount} / {totalObjectives} objectifs accomplis
      </p>
      <span
        className={`quest-journal-status-pill quest-journal-status-pill--${status}`}
        aria-label={statusAriaLabel}
      >
        {status === "ongoing" ? (
          <>
            {statusLabel}
            <span className="quest-journal-status-dots" aria-hidden="true">
              <span className="quest-journal-status-dot">.</span>
              <span className="quest-journal-status-dot">.</span>
              <span className="quest-journal-status-dot">.</span>
            </span>
          </>
        ) : (
          statusLabel
        )}
      </span>
    </div>
  );
}

function QuestObjectiveRow({
  objective,
  isActive,
  onTodayConfirmed,
  rowRef,
  completedObjectiveDisplay = QUEST_COMPLETED_OBJECTIVE_DISPLAY.CHECK,
}) {
  const isCompleted = objective.status === "completed";
  const progressLabel = formatQuestProgressLabel(objective);
  const resolvedLabel = splitCommonGemBourseNote(resolveQuestObjectiveLabel(objective));
  const useStrikethrough =
    isCompleted &&
    completedObjectiveDisplay === QUEST_COMPLETED_OBJECTIVE_DISPLAY.STRIKETHROUGH;
  const showCheck =
    isCompleted && completedObjectiveDisplay === QUEST_COMPLETED_OBJECTIVE_DISPLAY.CHECK;
  const showReadingHonorSelect =
    isActive &&
    objective.categoryId === "reading" &&
    !isCompleted &&
    !objective.honorLocked;
  const metamorph = objective.metamorph || null;

  return (
    <li
      className={`quest-objective-row quest-objective-row--${
        objective.status || "pending"
      }`}
    >
      <div className="quest-objective-row-main">
        <span className="quest-objective-row-label-line">
          <span
            ref={rowRef}
            className={`quest-objective-row-label${
              useStrikethrough ? " quest-objective-row-label--completed" : ""
            }`}
          >
            {resolvedLabel.text}
            <span className="quest-objective-row-progress"> ({progressLabel})</span>
          </span>
          {resolvedLabel.note ? (
            <VocabInfoTip
              className="quest-objective-bourse-tip"
              tooltip={resolvedLabel.note}
              ariaLabel={resolvedLabel.note}
              placement="above"
            />
          ) : null}
        </span>
        {metamorph ? (
          <MenuIconTooltip
            portal
            placement="above"
            className="quest-objective-metamorph-tip"
            tooltip={
              <>
                Objectif obtenu grâce à la Liqueur du Métamorphe.
                {metamorph.replacedLabel ? (
                  <>
                    <br />
                    Remplace&nbsp;: «&nbsp;{metamorph.replacedLabel}&nbsp;»
                  </>
                ) : null}
              </>
            }
          >
            <span
              className="quest-objective-metamorph-badge"
              tabIndex={0}
              role="img"
              aria-label={`Objectif remplacé par la Liqueur du Métamorphe${
                metamorph.replacedLabel ? ` (remplace : ${metamorph.replacedLabel})` : ""
              }`}
            >
              <img src={liqueurMetamorpheImg} alt="" draggable={false} />
            </span>
          </MenuIconTooltip>
        ) : null}
        {showReadingHonorSelect ? (
          <ReadingHonorSelect objective={objective} onConfirmed={onTodayConfirmed} />
        ) : null}
      </div>
      {showCheck ? (
        <span className="quest-objective-row-status">
          <CircleCheckIcon
            className="quest-objective-row-check quest-objective-row-check--done"
            size={22}
          />
        </span>
      ) : (
        <span className="quest-objective-row-status" aria-hidden="true" />
      )}
    </li>
  );
}

function mergeTodayIntoJournal(entries, todayPayload, activeDate) {
  if (!todayPayload?.date) {
    return entries;
  }

  const nextEntries = [...(entries || [])];
  const index = nextEntries.findIndex((entry) => entry.date === todayPayload.date);
  const mergedEntry = {
    ...(index >= 0 ? nextEntries[index] : {}),
    ...todayPayload,
    isActive: todayPayload.date === activeDate,
    dialogue: todayPayload.dialogue || nextEntries[index]?.dialogue || null,
  };

  if (index >= 0) {
    nextEntries[index] = mergedEntry;
  } else {
    nextEntries.unshift(mergedEntry);
  }

  return nextEntries.sort((left, right) => right.date.localeCompare(left.date));
}

function formatCharacterDisplayName(name) {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "Guide";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

const QUEST_SKELETON_LIST_WIDTHS = [
  "calc(74% - 5px)",
  "calc(86% - 5px)",
  "calc(62% - 5px)",
  "calc(80% - 5px)",
  "calc(58% - 5px)",
  "calc(90% - 5px)",
  "calc(70% - 5px)",
  "calc(78% - 5px)",
  "calc(64% - 5px)",
  "calc(84% - 5px)",
  "calc(68% - 5px)",
  "calc(76% - 5px)",
  "calc(72% - 5px)",
  "calc(88% - 5px)",
];
const QUEST_SKELETON_INTRO_LINES = [false, false, false, false, true];
const QUEST_SKELETON_OBJECTIVE_WIDTHS = ["47%", "43%", "50%", "38%"];

function QuestJournalSkeleton() {
  return (
    <div className="quest-journal-frame quest-journal-frame--skeleton" aria-hidden="true">
      <aside className="quest-journal-sidebar">
        <div className="quest-journal-sidebar-viewport">
          <div className="quest-journal-scroll-scroller">
            <div className="quest-journal-sidebar-scroll">
              <ul className="quest-journal-list">
                {QUEST_SKELETON_LIST_WIDTHS.map((width, index) => (
                  <li key={index}>
                    <div className="quest-journal-list-item">
                      <span className="quest-skel quest-skel--list-title" style={{ width }} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </aside>
      <div className="quest-journal-divider" />
      <article className="quest-journal-detail">
        <div className="quest-journal-detail-sticky">
          <div className="quest-journal-detail-header">
            <div className="quest-journal-portrait">
              <span className="quest-skel quest-skel--fill" />
            </div>
            <div className="quest-journal-detail-heading">
              <span className="quest-skel quest-skel--title" />
              <span className="quest-skel quest-skel--meta" />
              <span className="quest-skel quest-skel--meta quest-skel--meta-short" />
            </div>
            <div className="quest-journal-detail-progress">
              <div className="quest-journal-progress">
                <div className="quest-journal-progress-row">
                  {[0, 1, 2, 3].map((index) => (
                    <div key={index} className="quest-journal-progress-unit">
                      {index > 0 ? (
                        <span className="quest-journal-progress-bridge" aria-hidden="true" />
                      ) : null}
                      <span className="quest-journal-progress-slot" />
                    </div>
                  ))}
                </div>
                <p className="quest-journal-progress-label">
                  <span className="quest-journal-progress-label__metrics">
                    0 / 4 objectifs accomplis
                  </span>
                  <span className="quest-skel quest-skel--progress-label" />
                </p>
                <span className="quest-journal-status-pill">
                  En cours
                  <span className="quest-journal-status-dots" aria-hidden="true">
                    <span className="quest-journal-status-dot">.</span>
                    <span className="quest-journal-status-dot">.</span>
                    <span className="quest-journal-status-dot">.</span>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="quest-journal-detail-viewport">
          <div className="quest-journal-scroll-scroller">
            <div className="quest-journal-detail-scroll">
              <div className="quest-journal-detail-body">
                <section className="quest-journal-section">
                  <div className="quest-journal-intro-ornament" />
                  <span className="quest-skel quest-skel--section-title" />
                  <div className="quest-skel-intro">
                    {QUEST_SKELETON_INTRO_LINES.map((isLast, index) => (
                      <span
                        key={index}
                        className={`quest-skel quest-skel--intro-line${
                          isLast ? " quest-skel--intro-line-last" : ""
                        }`}
                      />
                    ))}
                  </div>
                </section>
                <section className="quest-journal-section">
                  <div className="quest-journal-section-heading">
                    <span className="quest-skel quest-skel--section-title quest-skel--section-title-sm" />
                    <div className="quest-journal-section-heading-aside">
                      <span className="quest-metamorph-trigger quest-metamorph-trigger--skel" />
                    </div>
                  </div>
                  <ul className="quest-objective-bullet-list">
                    {QUEST_SKELETON_OBJECTIVE_WIDTHS.map((width, index) => (
                      <li key={index} className="quest-objective-row">
                        <span className="quest-skel quest-skel--objective" style={{ width }} />
                        <span className="quest-skel quest-skel--check" />
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function QuestJournalList({ entries, selectedDate, onSelect, itemRefs }) {
  if (!entries.length) {
    return <p className="quest-view-empty">Aucune quête dans le journal.</p>;
  }

  return (
    <ul className="quest-journal-list">
      {entries.map((entry, index) => {
        const title = entry.dialogue?.title || `Quête du ${entry.date}`;
        const isActive = selectedDate === entry.date;

        return (
          <li key={entry.date}>
            <button
              type="button"
              ref={(element) => {
                itemRefs.current[index] = element;
              }}
              className={`quest-journal-list-item${
                isActive ? " quest-journal-list-item--active" : ""
              }`}
              onClick={() => onSelect(entry.date)}
            >
              {title}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function QuestJournalDetail({
  entry,
  onTodayConfirmed,
  playerName,
  onNavigateToTreasures,
  onRewardsClaimed,
  detailScrollAnchor = QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS.FREE,
  completedObjectiveDisplay = QUEST_COMPLETED_OBJECTIVE_DISPLAY.CHECK,
}) {
  const [claiming, setClaiming] = useState(false);
  const [chestModal, setChestModal] = useState(null);
  const [metamorphModalOpen, setMetamorphModalOpen] = useState(false);
  const [metamorphStock, setMetamorphStock] = useState(null);
  const metamorphDebitRef = useRef(new Set());
  const detailScrollRef = useRef(null);
  const thirdObjectiveRef = useRef(null);
  const pendingDetailRestoreRef = useRef(true);
  const detailDateRef = useRef(null);
  const creditedClaimRef = useRef(new Set());
  const detailPersistTimerRef = useRef(null);
  const lastDetailScrollTopRef = useRef(0);
  const detailSnapToBottomRef = useRef(false);
  const anchorSessionRef = useRef(false);
  const anchorStableRef = useRef(0);
  const lastAnchoredTopRef = useRef(null);
  const anchorRafRef = useRef(null);
  const anchorStartRef = useRef(0);
  const isFreeScroll = detailScrollAnchor === QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS.FREE;
  const {
    trackRef: detailTrackRef,
    scrollbar: detailScrollbar,
    updateScrollbar: updateDetailScrollbar,
    handleThumbPointerDown: handleDetailThumbPointerDown,
    handleTrackPointerDown: handleDetailTrackPointerDown,
  } = useCustomScrollbar(detailScrollRef);

  useEffect(() => {
    if (!entry?.date) return;
    if (detailDateRef.current !== entry.date) {
      detailDateRef.current = entry.date;
      pendingDetailRestoreRef.current = isFreeScroll;
      lastDetailScrollTopRef.current = 0;
      detailSnapToBottomRef.current = false;
    }
  }, [entry?.date, isFreeScroll]);

  const applyScrollAnchorPass = useCallback(() => {
    const container = detailScrollRef.current;
    if (!container || isFreeScroll) return true;

    if (detailScrollAnchor === QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS.REWARDS) {
      const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
      if (Math.abs(maxTop - container.scrollTop) > 1) {
        container.scrollTop = maxTop;
        anchorStableRef.current = 0;
      } else {
        anchorStableRef.current += 1;
      }
      lastAnchoredTopRef.current = container.scrollTop;
      return anchorStableRef.current >= 3;
    }

    if (detailScrollAnchor === QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS.OBJECTIVES) {
      const target = container.querySelector('[data-scroll-anchor="objectives"]');
      if (!target) {
        anchorStableRef.current = 0;
        return false;
      }

      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
      const desired = Math.max(
        0,
        Math.min(container.scrollTop + (targetRect.top - containerRect.top) - 8, maxTop)
      );

      if (Math.abs(desired - container.scrollTop) > 1) {
        container.scrollTop = desired;
        anchorStableRef.current = 0;
      } else {
        anchorStableRef.current += 1;
      }
      lastAnchoredTopRef.current = container.scrollTop;
      return anchorStableRef.current >= 3;
    }

    return true;
  }, [detailScrollAnchor, isFreeScroll]);

  useEffect(() => {
    if (!entry?.date || isFreeScroll) return undefined;

    anchorSessionRef.current = true;
    anchorStartRef.current =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    anchorStableRef.current = 0;
    lastAnchoredTopRef.current = null;
    cancelAnimationFrame(anchorRafRef.current);

    const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
    const MAX_DURATION_MS = 2500;

    const tick = () => {
      if (!anchorSessionRef.current) return;
      const container = detailScrollRef.current;

      if (
        lastAnchoredTopRef.current != null &&
        container &&
        Math.abs(container.scrollTop - lastAnchoredTopRef.current) > 2
      ) {
        anchorSessionRef.current = false;
        return;
      }

      const done = applyScrollAnchorPass();
      updateDetailScrollbar();
      if (done || now() - anchorStartRef.current > MAX_DURATION_MS) {
        anchorSessionRef.current = false;
        pendingDetailRestoreRef.current = false;
        return;
      }
      anchorRafRef.current = requestAnimationFrame(tick);
    };

    anchorRafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(anchorRafRef.current);
      anchorSessionRef.current = false;
    };
  }, [
    entry?.date,
    entry?.dialogue?.questId,
    entry?.objectives?.length,
    entry?.dialogue?.intro,
    detailScrollAnchor,
    isFreeScroll,
    applyScrollAnchorPass,
    updateDetailScrollbar,
  ]);

  // Auto-rattrapage : si une récompense a déjà été réclamée (rewardClaimedAt)
  // mais que le crédit pierres/potions n'a pas abouti (backend éteint au moment
  // du claim, re-roll sur la même date, etc.), on retente le crédit. Le backend
  // est idempotent par claim (date + horodatage), donc l'appel est sans risque.
  const rewards = entry?.rewards;
  const claimedAt = rewards?.claimedAt || null;
  useEffect(() => {
    if (!entry?.date || rewards?.status !== "claimed") return;
    const grants = Array.isArray(rewards?.grants) ? rewards.grants : [];
    if (grants.length === 0) return;

    const claimKey = `${entry.date}#${claimedAt || ""}`;
    if (creditedClaimRef.current.has(claimKey)) return;
    creditedClaimRef.current.add(claimKey);

    creditQuestRewardToInventory(entry.date, grants, claimedAt).catch((err) => {
      console.error("Auto-crédit des lots de quête :", err);
    });
  }, [entry?.date, rewards?.status, claimedAt, rewards?.grants]);

  // Rattrapage du débit de la Liqueur du Métamorphe : si le remplacement a été
  // enregistré côté quêtes mais que l'inventaire n'a pas pu être débité (réseau,
  // backend éteint), on rejoue le débit. Idempotent par jeton côté serveur.
  const metamorphDebitToken = entry?.metamorph?.debitToken || null;
  useEffect(() => {
    if (!entry?.date || !metamorphDebitToken) return;
    const debitKey = `${entry.date}#${metamorphDebitToken}`;
    if (metamorphDebitRef.current.has(debitKey)) return;
    metamorphDebitRef.current.add(debitKey);

    debitMetamorphPotion(entry.date, metamorphDebitToken).catch((err) => {
      console.error("Rattrapage du débit de la Liqueur du Métamorphe :", err);
    });
  }, [entry?.date, metamorphDebitToken]);

  const metamorphButtonEligible =
    Boolean(entry?.isActive) &&
    !entry?.finalizedAt &&
    !entry?.allCompleted &&
    !entry?.metamorph?.usedAt;

  useEffect(() => {
    if (!metamorphButtonEligible) {
      setMetamorphStock(null);
      return undefined;
    }

    let cancelled = false;
    setMetamorphStock(null);
    fetchPotionCounts()
      .then((counts) => {
        if (!cancelled) setMetamorphStock(Number(counts[METAMORPH_POTION_ID]) || 0);
      })
      .catch(() => {
        if (!cancelled) setMetamorphStock(0);
      });

    return () => {
      cancelled = true;
    };
  }, [metamorphButtonEligible, entry?.date]);

  const flushDetailScroll = useCallback(() => {
    if (!isFreeScroll) return;
    const scrollEl = detailScrollRef.current;
    const date = detailDateRef.current;
    if (scrollEl && date && scrollEl.clientHeight > 0) {
      writeQuestJournalDetailScroll(date, scrollEl.scrollTop);
    }
  }, [isFreeScroll]);

  const scrollDetailToBottom = useCallback(
    (behavior = "smooth") => {
      const scrollEl = detailScrollRef.current;
      const date = detailDateRef.current;
      if (!scrollEl) return;

      pendingDetailRestoreRef.current = false;
      detailSnapToBottomRef.current = true;
      scrollEl.scrollTo({
        top: scrollEl.scrollHeight,
        behavior,
      });
      updateDetailScrollbar();

      if (date) {
        window.setTimeout(
          () => {
            writeQuestJournalDetailScroll(date, scrollEl.scrollTop);
            lastDetailScrollTopRef.current = scrollEl.scrollTop;
          },
          behavior === "smooth" ? 480 : 0
        );
      }
    },
    [updateDetailScrollbar]
  );

  const maybeSnapDetailScrollToRewards = useCallback(
    (behavior = "smooth", scrollDelta = 0) => {
      if (!isFreeScroll || detailSnapToBottomRef.current) return;

      const scrollingDown = scrollDelta > 0.5;
      const jumpedDown = scrollDelta > 48;
      if (!scrollingDown && !jumpedDown) return;

      const scrollEl = detailScrollRef.current;
      const objectiveEl = thirdObjectiveRef.current;
      if (!scrollEl || !objectiveEl) return;

      const nearBottom =
        scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 8;
      if (nearBottom) return;

      const scrollRect = scrollEl.getBoundingClientRect();
      const objectiveRect = objectiveEl.getBoundingClientRect();
      const viewportPastThirdObjective =
        scrollRect.bottom > objectiveRect.bottom + 1;

      if (viewportPastThirdObjective) {
        scrollDetailToBottom(behavior);
      }
    },
    [isFreeScroll, scrollDetailToBottom]
  );

  const tryRestoreDetailScroll = useCallback(() => {
    if (!pendingDetailRestoreRef.current) return;
    const scrollEl = detailScrollRef.current;
    const date = detailDateRef.current;
    if (!scrollEl || !date || scrollEl.clientHeight === 0) return;

    scrollEl.scrollTop = readQuestJournalDetailScroll(date);
    pendingDetailRestoreRef.current = false;
    lastDetailScrollTopRef.current = scrollEl.scrollTop;
    updateDetailScrollbar();
  }, [updateDetailScrollbar]);

  useEffect(() => {
    if (!entry?.date || !isFreeScroll) return undefined;

    let frame1;
    let frame2;
    frame1 = window.requestAnimationFrame(() => {
      tryRestoreDetailScroll();
      frame2 = window.requestAnimationFrame(() => tryRestoreDetailScroll());
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [entry?.date, entry?.dialogue?.questId, isFreeScroll, tryRestoreDetailScroll]);

  useEffect(() => {
    const scrollEl = detailScrollRef.current;
    if (!scrollEl) return undefined;

    const observer = new ResizeObserver(() => {
      if (isFreeScroll) {
        tryRestoreDetailScroll();
      } else if (!anchorSessionRef.current) {
        applyScrollAnchorPass();
      }
      updateDetailScrollbar();
    });
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [
    entry?.date,
    isFreeScroll,
    tryRestoreDetailScroll,
    applyScrollAnchorPass,
    updateDetailScrollbar,
  ]);

  useEffect(() => {
    const onBeforeUnload = () => flushDetailScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushDetailScroll();
    };
  }, [flushDetailScroll]);

  const handleDetailScroll = useCallback(() => {
    const scrollEl = detailScrollRef.current;
    const date = detailDateRef.current;
    if (!scrollEl || !date) return;

    updateDetailScrollbar();

    const scrollTop = scrollEl.scrollTop;
    const scrollDelta = scrollTop - lastDetailScrollTopRef.current;
    const nearBottom =
      scrollEl.scrollHeight - scrollTop - scrollEl.clientHeight < 8;

    if (detailSnapToBottomRef.current) {
      if (nearBottom) {
        detailSnapToBottomRef.current = false;
      }
    } else if (isFreeScroll) {
      maybeSnapDetailScrollToRewards("smooth", scrollDelta);
    }

    lastDetailScrollTopRef.current = scrollTop;

    if (!isFreeScroll) return;

    if (detailPersistTimerRef.current) {
      window.clearTimeout(detailPersistTimerRef.current);
    }
    detailPersistTimerRef.current = window.setTimeout(() => {
      writeQuestJournalDetailScroll(date, scrollEl.scrollTop);
    }, 120);
  }, [updateDetailScrollbar, maybeSnapDetailScrollToRewards, isFreeScroll]);

  useEffect(() => {
    updateDetailScrollbar();
  }, [entry?.date, entry?.dialogue?.questId, updateDetailScrollbar]);

  const characterId = entry?.dialogue?.characterId;
  const characterName =
    entry?.dialogue?.characterName ||
    getQuestCharacter(characterId)?.displayName ||
    "Guide";

  const handleOpenChest = useCallback(async () => {
    if (!entry?.date || claiming) return;

    setClaiming(true);
    try {
      const result = await claimQuestReward(entry.date);
      onRewardsClaimed?.(result);

      if (result?.claimed && Array.isArray(result.grants)) {
        try {
          const claimToken =
            result.rewards?.claimedAt ||
            result.payload?.rewards?.claimedAt ||
            null;
          await creditQuestRewardToInventory(entry.date, result.grants, claimToken);
        } catch (creditErr) {
          console.error("Crédit des lots de quête :", creditErr);
        }
      }

      setChestModal({
        characterId,
        characterName,
        grants: result.grants || [],
        relicWon: Boolean(result.relicWon),
        relicId: result.relicId || "",
        relicLabel: result.relicLabel || "",
        collectionId: result.collectionId || "",
        collectionTitle: result.collectionTitle || "",
      });
    } catch (err) {
      console.error("Ouverture du coffre :", err);
    } finally {
      setClaiming(false);
    }
  }, [entry?.date, claiming, characterId, characterName, onRewardsClaimed]);

  const handleCloseChestModal = useCallback(() => {
    setChestModal(null);
  }, []);

  if (!entry) {
    return (
      <div className="quest-journal-detail quest-journal-detail--empty">
        <p className="quest-view-empty">Sélectionnez une quête dans le journal.</p>
      </div>
    );
  }

  const dialogue = entry.dialogue;
  const character = getQuestCharacter(characterId);
  const portrait = getQuestCharacterPortrait(characterId);
  const title = dialogue?.title || "Quête sans titre";
  const intro = personalizeQuestIntro(dialogue?.intro, playerName);
  const objectives = entry.objectives || [];
  const thirdObjectiveIndex = Math.min(2, Math.max(objectives.length - 1, 0));
  const showChestOpenButton =
    entry.allCompleted && entry.rewards?.status === "openable";
  const metamorphUsed = Boolean(entry.metamorph?.usedAt);
  const showMetamorphButton =
    Boolean(entry.isActive) && !entry.finalizedAt && !entry.allCompleted && !metamorphUsed;

  return (
    <article className="quest-journal-detail">
      <div className="quest-journal-detail-sticky">
        <div className="quest-journal-detail-header">
          {portrait ? (
            <div className="quest-journal-portrait">
              <img src={portrait} alt="" aria-hidden="true" draggable={false} />
            </div>
          ) : (
            <div className="quest-journal-portrait quest-journal-portrait--placeholder" aria-hidden="true" />
          )}
          <div className="quest-journal-detail-heading">
            <h3 className="quest-journal-title">{title}</h3>
            <p className="quest-journal-date">
              Quête du {formatQuestJournalDate(entry.date)}
            </p>
            <p className="quest-journal-character">
              <em>Proposée par</em> {formatCharacterDisplayName(characterName)}
            </p>
          </div>
          <div className="quest-journal-detail-progress">
            <QuestProgressBar
              completedCount={entry.completedCount || 0}
              totalObjectives={entry.totalObjectives || objectives.length || 4}
              isActive={Boolean(entry.isActive)}
            />
          </div>
        </div>
      </div>

      <div className="quest-journal-detail-viewport">
        <div className="quest-journal-scroll-scroller">
          <div
            ref={detailScrollRef}
            className="quest-journal-detail-scroll"
            onScroll={handleDetailScroll}
          >
            <div className="quest-journal-detail-body">
              {intro ? (
                <section className="quest-journal-section">
                  <div className="quest-journal-intro-ornament" aria-hidden="true" />
                  <h4 className="quest-journal-section-title">Quête du jour</h4>
                  <p className="quest-journal-intro">{intro}</p>
                </section>
              ) : null}

              <section className="quest-journal-section" data-scroll-anchor="objectives">
                <div className="quest-journal-section-heading">
                  <h4 className="quest-journal-section-title">Objectifs</h4>
                  <div className="quest-journal-section-heading-aside">
                  {showMetamorphButton ? (
                    metamorphStock == null ? (
                      <span
                        className="quest-metamorph-trigger quest-metamorph-trigger--skel"
                        aria-hidden="true"
                      />
                    ) : (
                    <MenuIconTooltip
                      portal
                      placement="left"
                      className="quest-metamorph-trigger-tip"
                      tooltip={
                        metamorphStock > 0
                          ? "Liqueur du Métamorphe : remplacer un objectif non entamé"
                          : "Vous ne possédez pas de Liqueur du Métamorphe"
                      }
                    >
                      <button
                        type="button"
                        className="quest-metamorph-trigger"
                        onClick={() => {
                          if (metamorphStock > 0) setMetamorphModalOpen(true);
                        }}
                        disabled={metamorphStock < 1}
                        aria-label={
                          metamorphStock > 0
                            ? "Utiliser la Liqueur du Métamorphe"
                            : "Liqueur du Métamorphe : aucune en stock"
                        }
                      >
                        <img src={liqueurMetamorpheImg} alt="" draggable={false} />
                      </button>
                    </MenuIconTooltip>
                    )
                  ) : null}
                  {metamorphUsed ? (
                    <MenuIconTooltip
                      portal
                      placement="left"
                      className="quest-metamorph-trigger-tip"
                      tooltip="Liqueur du Métamorphe utilisée pour cette quête"
                    >
                      <span
                        className="quest-metamorph-trigger quest-metamorph-trigger--used"
                        tabIndex={0}
                        role="img"
                        aria-label="Liqueur du Métamorphe utilisée pour cette quête"
                      >
                        <img src={liqueurMetamorpheImg} alt="" draggable={false} />
                      </span>
                    </MenuIconTooltip>
                  ) : null}
                  </div>
                </div>
                <ul className="quest-objective-bullet-list">
                  {objectives.map((objective, index) => (
                    <QuestObjectiveRow
                      key={objective.id}
                      objective={objective}
                      isActive={entry.isActive}
                      onTodayConfirmed={onTodayConfirmed}
                      rowRef={index === thirdObjectiveIndex ? thirdObjectiveRef : undefined}
                      completedObjectiveDisplay={completedObjectiveDisplay}
                    />
                  ))}
                </ul>
              </section>

              <section className="quest-journal-section quest-journal-section--rewards">
                <h4 className="quest-journal-section-title">Récompenses</h4>
                <QuestJournalRewards
                  rewards={entry.rewards}
                  allCompleted={entry.allCompleted}
                  onOpenChest={handleOpenChest}
                  claiming={claiming}
                />
              </section>

              <div
                className={[
                  "quest-journal-intro-ornament",
                  "quest-journal-intro-ornament--footer",
                  showChestOpenButton && "quest-journal-intro-ornament--footer-with-button",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-hidden="true"
              />
            </div>
          </div>
          <QuestJournalScrollbar
            trackRef={detailTrackRef}
            scrollbar={detailScrollbar}
            variant="detail"
            onTrackPointerDown={handleDetailTrackPointerDown}
            onThumbPointerDown={handleDetailThumbPointerDown}
          />
        </div>
        <div className="quest-journal-detail-fade quest-journal-detail-fade--top" aria-hidden="true" />
        <div className="quest-journal-detail-fade quest-journal-detail-fade--bottom" aria-hidden="true" />
      </div>

      <QuestChestRewardModal
        open={Boolean(chestModal)}
        characterId={chestModal?.characterId}
        characterName={chestModal?.characterName}
        grants={chestModal?.grants || []}
        relicWon={chestModal?.relicWon}
        relicId={chestModal?.relicId}
        relicLabel={chestModal?.relicLabel}
        collectionId={chestModal?.collectionId}
        collectionTitle={chestModal?.collectionTitle}
        onClose={handleCloseChestModal}
        onNavigateToTreasures={onNavigateToTreasures}
      />

      <QuestMetamorphModal
        open={metamorphModalOpen}
        date={entry.date}
        onClose={() => setMetamorphModalOpen(false)}
        onApplied={async (payload) => {
          await onTodayConfirmed?.(payload);
          dispatchNotificationsChanged();
          notifyQuestProgressChanged();
        }}
      />
    </article>
  );
}

export default function QuestView({
  playerName = "",
  onCountdownChange,
  onNavigateToTreasures,
  searchQuery = "",
  searchScope = QUEST_JOURNAL_SEARCH_SCOPES.TITLE,
  journalDetailScrollAnchor = QUEST_JOURNAL_DETAIL_SCROLL_ANCHORS.FREE,
  completedObjectiveDisplay = QUEST_COMPLETED_OBJECTIVE_DISPLAY.CHECK,
}) {
  const [journal, setJournal] = useState({ entries: [], activeDate: null, nextResetAt: null });
  const [selectedDate, setSelectedDate] = useState(() => readQuestJournalSelectedDate());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [journalFilters, setJournalFilters] = useState(() =>
    sanitizeQuestJournalFilters(readQuestJournalFilters() || DEFAULT_QUEST_JOURNAL_FILTERS)
  );
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const journalFiltersRef = useRef(journalFilters);
  const sidebarScrollRef = useRef(null);
  const sidebarItemRefs = useRef({});
  const pendingSidebarRestoreRef = useRef(true);
  const sidebarPersistTimerRef = useRef(null);
  const {
    trackRef: sidebarTrackRef,
    scrollbar: sidebarScrollbar,
    updateScrollbar: updateSidebarScrollbar,
    handleThumbPointerDown: handleSidebarThumbPointerDown,
    handleTrackPointerDown: handleSidebarTrackPointerDown,
  } = useCustomScrollbar(sidebarScrollRef);
  const [sidebarScrollFades, setSidebarScrollFades] = useState({
    top: false,
    bottom: false,
  });
  const selectedDateRef = useRef(selectedDate);
  const journalEntriesRef = useRef(journal.entries);

  selectedDateRef.current = selectedDate;
  journalEntriesRef.current = journal.entries;
  journalFiltersRef.current = journalFilters;

  const filterSubActive = isQuestJournalFilterActive(journalFilters);
  const normalizedSearchQuery = useMemo(
    () => normalizeRegisterSearch(searchQuery),
    [searchQuery]
  );
  const searchSubActive = Boolean(normalizedSearchQuery);
  const visibleEntries = useMemo(
    () =>
      journal.entries.filter((entry) =>
        matchesQuestJournalSearch(entry, normalizedSearchQuery, searchScope, playerName)
      ),
    [journal.entries, normalizedSearchQuery, searchScope, playerName]
  );

  const updateSidebarScrollUi = useCallback(() => {
    const scrollEl = sidebarScrollRef.current;
    if (!scrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollEl;
    const threshold = 6;
    const hasOverflow = scrollHeight > clientHeight + 1;

    setSidebarScrollFades({
      top: hasOverflow && scrollTop > threshold,
      bottom: hasOverflow && scrollTop + clientHeight < scrollHeight - threshold,
    });

    updateSidebarScrollbar();
  }, [updateSidebarScrollbar]);

  const flushSidebarScroll = useCallback(() => {
    const scrollEl = sidebarScrollRef.current;
    if (scrollEl && scrollEl.clientHeight > 0) {
      writeQuestJournalSidebarScroll(scrollEl.scrollTop);
    }
  }, []);

  const tryRestoreSidebarScroll = useCallback(() => {
    if (!pendingSidebarRestoreRef.current) return;

    const scrollEl = sidebarScrollRef.current;
    if (!scrollEl || scrollEl.clientHeight === 0) return;

    const entries = journalEntriesRef.current;
    const activeDate = selectedDateRef.current;
    const selectedIndex = entries.findIndex((entry) => entry.date === activeDate);

    if (selectedIndex >= 0 && !sidebarItemRefs.current[selectedIndex]) return;

    const savedScrollTop = readQuestJournalSidebarScroll();
    if (savedScrollTop > 0) {
      scrollEl.scrollTop = savedScrollTop;
    }

    const selectedItem = selectedIndex >= 0 ? sidebarItemRefs.current[selectedIndex] : null;
    if (selectedItem) {
      const listRect = scrollEl.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();
      const fullyVisible =
        itemRect.top >= listRect.top && itemRect.bottom <= listRect.bottom;

      if (!fullyVisible) {
        const delta =
          itemRect.top - listRect.top - scrollEl.clientHeight / 2 + itemRect.height / 2;
        scrollEl.scrollTop += delta;
      }
    }

    pendingSidebarRestoreRef.current = false;
    updateSidebarScrollUi();
  }, [updateSidebarScrollUi]);

  const handleSelectDate = useCallback((date) => {
    setSelectedDate(date);
    writeQuestJournalSelectedDate(date);
    if (date && date === journal.activeDate) {
      writeQuestJournalLastSeenActiveDate(date);
    }
    flushSidebarScroll();
  }, [flushSidebarScroll, journal.activeDate]);

  const navigateQuest = useCallback(
    (direction) => {
      if (!visibleEntries.length) return;

      const currentIndex = visibleEntries.findIndex((entry) => entry.date === selectedDate);
      const baseIndex = currentIndex >= 0 ? currentIndex : 0;
      const nextIndex =
        direction === "down"
          ? (baseIndex + 1) % visibleEntries.length
          : (baseIndex - 1 + visibleEntries.length) % visibleEntries.length;
      const next = visibleEntries[nextIndex];
      if (!next) return;

      handleSelectDate(next.date);
      window.requestAnimationFrame(() => {
        const item = sidebarItemRefs.current[nextIndex];
        if (!item) return;
        item.focus({ preventScroll: true });
        item.scrollIntoView({ block: "nearest" });
      });
    },
    [visibleEntries, selectedDate, handleSelectDate]
  );

  const handleSidebarScroll = useCallback(() => {
    const scrollEl = sidebarScrollRef.current;
    if (!scrollEl) return;

    updateSidebarScrollUi();

    if (sidebarPersistTimerRef.current) {
      window.clearTimeout(sidebarPersistTimerRef.current);
    }
    sidebarPersistTimerRef.current = window.setTimeout(() => {
      writeQuestJournalSidebarScroll(scrollEl.scrollTop);
    }, 120);
  }, [updateSidebarScrollUi]);

  useEffect(() => {
    if (loading) return undefined;

    let frame1;
    let frame2;
    frame1 = window.requestAnimationFrame(() => {
      tryRestoreSidebarScroll();
      frame2 = window.requestAnimationFrame(() => tryRestoreSidebarScroll());
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      if (frame2) window.cancelAnimationFrame(frame2);
    };
  }, [loading, journal.entries, selectedDate, tryRestoreSidebarScroll]);

  useEffect(() => {
    const scrollEl = sidebarScrollRef.current;
    if (!scrollEl) return undefined;

    const observer = new ResizeObserver(() => {
      tryRestoreSidebarScroll();
      updateSidebarScrollUi();
    });
    observer.observe(scrollEl);
    return () => observer.disconnect();
  }, [tryRestoreSidebarScroll, updateSidebarScrollUi]);

  useEffect(() => {
    if (loading) return;
    updateSidebarScrollUi();
  }, [loading, journal.entries.length, selectedDate, updateSidebarScrollUi]);

  useEffect(() => {
    const onBeforeUnload = () => flushSidebarScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushSidebarScroll();
      pendingSidebarRestoreRef.current = true;
    };
  }, [flushSidebarScroll]);

  const applyTodayPayload = useCallback(async (todayPayload) => {
    setJournal((current) => {
      const activeDate = todayPayload?.date || current.activeDate;
      return {
        ...current,
        activeDate,
        nextResetAt: todayPayload?.nextResetAt || current.nextResetAt,
        entries: mergeTodayIntoJournal(current.entries, todayPayload, activeDate),
      };
    });

    await processQuestCompletionSideEffects(todayPayload);
  }, []);

  const loadJournal = useCallback(async ({ silent = false, selectFirstEntry = false } = {}) => {
    if (!QUESTS_API_URL) {
      setError("Configurez REACT_APP_QUESTS_API_URL dans .env.local puis redémarrez l'app.");
      setLoading(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    const activeFilters = journalFiltersRef.current;

    try {
      const [journalData, todayData] = await Promise.all([
        fetchQuestJournal(activeFilters),
        fetchTodayQuests(),
      ]);

      let entries = journalData.entries || [];
      if (todayData.found) {
        const shouldMergeToday =
          !isQuestJournalFilterActive(activeFilters) ||
          entries.some((entry) => entry.date === todayData.date);

        if (shouldMergeToday) {
          entries = mergeTodayIntoJournal(entries, todayData, journalData.activeDate);
        }
        await processQuestCompletionSideEffects(todayData);
      }

      setJournal({
        entries,
        activeDate: journalData.activeDate,
        nextResetAt: todayData.nextResetAt || journalData.nextResetAt,
      });
      const selection = resolveQuestJournalSelectedDate({
        selectFirstEntry,
        currentDate: selectedDateRef.current,
        storedDate: readQuestJournalSelectedDate(),
        lastSeenActiveDate: readQuestJournalLastSeenActiveDate(),
        entries,
        activeDate: journalData.activeDate,
      });
      writeQuestJournalSelectedDate(selection.date);
      if (selection.markActiveSeen && journalData.activeDate) {
        writeQuestJournalLastSeenActiveDate(journalData.activeDate);
      }
      if (selection.switchedToUnseenActive) {
        pendingSidebarRestoreRef.current = true;
      }
      setSelectedDate(selection.date);
      setError("");
      if (!silent) {
        pendingSidebarRestoreRef.current = true;
      }
    } catch (err) {
      setError(err.message || "Impossible de charger le journal de quête.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRewardsClaimed = useCallback((claimResult) => {
    const date = claimResult?.date;
    const rewards = claimResult?.rewards;
    if (!date || !rewards) return;

    setJournal((current) => ({
      ...current,
      entries: current.entries.map((entry) =>
        entry.date === date
          ? {
              ...entry,
              rewards: {
                ...rewards,
                grants: claimResult.grants || rewards.grants,
                relicWon: claimResult.relicWon ?? rewards.relicWon,
                relicLabel: claimResult.relicLabel || rewards.relicLabel,
                collectionTitle: claimResult.collectionTitle || rewards.collectionTitle,
                status: "claimed",
              },
            }
          : entry
      ),
    }));
    notifyQuestProgressChanged();
  }, []);

  const handleTodayConfirmed = useCallback(
    async (todayPayload) => {
      await applyTodayPayload(todayPayload);
    },
    [applyTodayPayload]
  );

  const handleApplyJournalFilters = useCallback((nextFilters) => {
    const sanitized = sanitizeQuestJournalFilters(nextFilters);
    journalFiltersRef.current = prepareQuestJournalFiltersForApply(sanitized);
    setJournalFilters(sanitized);
    writeQuestJournalFilters(sanitized);
    pendingSidebarRestoreRef.current = true;
    loadJournal({ selectFirstEntry: true });
  }, [loadJournal]);

  useEffect(() => {
    if (loading || filterModalOpen || statsModalOpen) return undefined;

    const handleGlobalKeyDown = (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      if (tag === "textarea" || target?.isContentEditable) return;
      if (tag === "input") {
        const type = String(target.type || "text").toLowerCase();
        if (type !== "radio" && type !== "checkbox" && type !== "button") return;
      }
      if (tag === "select") return;
      if (!visibleEntries.length) return;

      event.preventDefault();
      navigateQuest(event.key === "ArrowDown" ? "down" : "up");
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [loading, filterModalOpen, statsModalOpen, visibleEntries.length, navigateQuest]);

  useEffect(() => {
    if (loading) return;

    setSelectedDate((current) => {
      if (current && visibleEntries.some((entry) => entry.date === current)) {
        return current;
      }

      const nextDate = visibleEntries[0]?.date || null;
      writeQuestJournalSelectedDate(nextDate);
      return nextDate;
    });
  }, [loading, normalizedSearchQuery, searchScope, visibleEntries]);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (QUESTS_API_URL) {
          await markQuestsRead();
          dispatchNotificationsChanged();
        }
      } catch (err) {
        console.warn("Marquage quêtes consultées :", err.message);
      }

      if (!cancelled) {
        loadJournal();
      }
    };

    init();

    const pollId = window.setInterval(() => {
      loadJournal({ silent: true });
    }, QUEST_POLL_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        loadJournal({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(pollId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleVisibility);
    };
  }, [loadJournal]);

  useEffect(() => {
    if (!journal.nextResetAt) {
      setCountdown(null);
      return undefined;
    }

    const updateCountdown = () => {
      setCountdown(formatQuestCountdown(journal.nextResetAt));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(intervalId);
  }, [journal?.nextResetAt]);

  const todayQuestCompleted = useMemo(() => {
    const todayEntry =
      journal.entries.find((entry) => entry?.isActive) ||
      journal.entries.find((entry) => entry?.date && entry.date === journal.activeDate) ||
      null;
    return Boolean(todayEntry?.allCompleted);
  }, [journal.entries, journal.activeDate]);

  useEffect(() => {
    if (!countdown) {
      onCountdownChange?.(null);
      return;
    }
    onCountdownChange?.({
      value: countdown,
      todayCompleted: todayQuestCompleted,
    });
  }, [countdown, todayQuestCompleted, onCountdownChange]);

  useEffect(() => {
    return () => onCountdownChange?.(null);
  }, [onCountdownChange]);

  const selectedEntry = useMemo(
    () => visibleEntries.find((entry) => entry.date === selectedDate) || null,
    [visibleEntries, selectedDate]
  );

  const showJournalFrame = !loading && !error && visibleEntries.length > 0;
  const showJournalSkeleton = loading && !error;

  return (
    <div
      className="quest-view quest-view--journal"
      aria-busy={showJournalSkeleton}
    >
      <header className="quest-view-header">
        <div className="quest-view-header__filter-wrap">
          <div className="quest-view-header__sum filterIcon">
            <Sigma
              size={38}
              color="#39210d"
              strokeWidth={1}
              onClick={() => setStatsModalOpen(true)}
              aria-label="Statistiques de quêtes"
              style={{ cursor: "pointer" }}
            />
          </div>
          <div className="quest-view-header__filter-group">
            <div className={`quest-view-header__filter filterIcon${filterSubActive ? " active" : ""}`}>
              <span className="menu-icon-badge-wrap">
                <Filter
                  size={38}
                  color="#39210d"
                  strokeWidth={1}
                  onClick={() => setFilterModalOpen(true)}
                  aria-label="Filtrer le journal de quêtes"
                  style={{ cursor: "pointer" }}
                />
                {filterSubActive ? (
                  <button
                    type="button"
                    className="menu-filter-clear"
                    aria-label="Retirer le filtre"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setFilterModalOpen(false);
                      handleApplyJournalFilters(DEFAULT_QUEST_JOURNAL_FILTERS);
                    }}
                  >
                    <X size={11} strokeWidth={3} aria-hidden="true" />
                  </button>
                ) : null}
              </span>
            </div>
            {showJournalSkeleton ? (
              <p className="quest-view-header__filter-count" aria-hidden="true">
                <span className="quest-view-header__filter-count-value">
                  <span className="quest-skel-count-metrics">00</span>
                  <span className="quest-skel quest-skel--count-value" />
                </span>
                <span className="quest-view-header__filter-count-label">
                  <span className="quest-skel-count-metrics">quêtes</span>
                  <span className="quest-skel quest-skel--count-label" />
                </span>
              </p>
            ) : (
              <p
                className={`quest-view-header__filter-count${
                  filterSubActive ? " quest-view-header__filter-count--active" : ""
                }`}
                aria-live="polite"
              >
                <span className="quest-view-header__filter-count-value">
                  {visibleEntries.length.toLocaleString("fr-FR")}
                </span>
                <span className="quest-view-header__filter-count-label">
                  {visibleEntries.length > 1 ? "quêtes" : "quête"}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="quest-view-header__title-wrap">
          <h2 className="quest-view-title">Journal de quête du Royaume</h2>
        </div>
      </header>

      <div className="quest-view-body quest-view-body--journal">
        {error ? <p className="quest-view-error">{error}</p> : null}

        {showJournalSkeleton ? <QuestJournalSkeleton /> : null}

        {!loading && !error && !journal.entries.length ? (
          <p className="quest-view-empty">
            {filterSubActive
              ? "Aucune quête ne correspond à ces filtres."
              : "Aucune quête disponible pour le moment."}
          </p>
        ) : null}

        {!loading && !error && journal.entries.length > 0 && !visibleEntries.length ? (
          <p className="quest-view-empty">
            {searchSubActive
              ? "Aucune quête ne correspond à cette recherche."
              : "Aucune quête disponible pour le moment."}
          </p>
        ) : null}

        {showJournalFrame ? (
          <div className="quest-journal-frame">
            <aside className="quest-journal-sidebar">
              <div className="quest-journal-sidebar-viewport">
                <div className="quest-journal-scroll-scroller">
                  <div
                    ref={sidebarScrollRef}
                    className="quest-journal-sidebar-scroll"
                    onScroll={handleSidebarScroll}
                  >
                    <QuestJournalList
                      entries={visibleEntries}
                      selectedDate={selectedDate}
                      onSelect={handleSelectDate}
                      itemRefs={sidebarItemRefs}
                    />
                  </div>
                  <QuestJournalScrollbar
                    trackRef={sidebarTrackRef}
                    scrollbar={sidebarScrollbar}
                    variant="sidebar"
                    onTrackPointerDown={handleSidebarTrackPointerDown}
                    onThumbPointerDown={handleSidebarThumbPointerDown}
                  />
                </div>
                {sidebarScrollFades.top ? (
                  <div
                    className="quest-journal-sidebar-fade quest-journal-sidebar-fade--top"
                    aria-hidden="true"
                  />
                ) : null}
                {sidebarScrollFades.bottom ? (
                  <div
                    className="quest-journal-sidebar-fade quest-journal-sidebar-fade--bottom"
                    aria-hidden="true"
                  />
                ) : null}
              </div>
            </aside>
            <div className="quest-journal-divider" aria-hidden="true" />
            <QuestJournalDetail
              entry={
                selectedEntry
                  ? {
                      ...selectedEntry,
                      objectives: selectedEntry.objectives?.map((objective) => ({
                        ...objective,
                      })),
                    }
                  : null
              }
              onTodayConfirmed={handleTodayConfirmed}
              playerName={playerName}
              onNavigateToTreasures={onNavigateToTreasures}
              onRewardsClaimed={handleRewardsClaimed}
              detailScrollAnchor={journalDetailScrollAnchor}
              completedObjectiveDisplay={completedObjectiveDisplay}
            />
          </div>
        ) : null}
      </div>

      <QuestJournalFilterModal
        open={filterModalOpen}
        value={journalFilters}
        onApply={handleApplyJournalFilters}
        onClose={() => setFilterModalOpen(false)}
      />

      <QuestJournalStatsModal
        open={statsModalOpen}
        entries={visibleEntries}
        onClose={() => setStatsModalOpen(false)}
      />
    </div>
  );
}
